/**
 * Utilities for handling multiple file URLs stored as JSON arrays in text columns.
 * Format: '["path1.pdf","path2.jpg"]' or legacy single path "path.pdf"
 */

const MAX_FILE_SIZE = 200 * 1024 * 1024; // 200MB
const MAX_FILES = 20;
const VALID_TYPES = [
  "application/pdf",
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/gif",
  "image/heic",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
];

export { MAX_FILE_SIZE, MAX_FILES, VALID_TYPES };

/** Parse a file_url field that may be a JSON array or a single path */
export function parseFileUrls(fileUrl: string | null | undefined): string[] {
  if (!fileUrl) return [];
  const trimmed = fileUrl.trim();
  if (trimmed.startsWith("[")) {
    try {
      const arr = JSON.parse(trimmed);
      if (Array.isArray(arr)) return arr.filter(Boolean);
    } catch {
      return [trimmed];
    }
  }
  return [trimmed];
}

/** Serialize an array of paths to a JSON string for storage */
export function serializeFileUrls(paths: string[]): string | null {
  if (paths.length === 0) return null;
  if (paths.length === 1) return paths[0]; // backward compat
  return JSON.stringify(paths);
}

/** Format file size in human readable format */
export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** Check if a file type is an image */
export function isImageFile(file: File | string): boolean {
  if (typeof file === "string") {
    return /\.(jpg|jpeg|png|webp|gif)$/i.test(file);
  }
  return file.type.startsWith("image/");
}

/** Validate files for upload, returns { valid, errors } */
export function validateFiles(
  newFiles: File[],
  existingCount: number
): { valid: File[]; errors: string[] } {
  const valid: File[] = [];
  const errors: string[] = [];

  for (const file of newFiles) {
    if (existingCount + valid.length >= MAX_FILES) {
      errors.push(`الحد الأقصى ${MAX_FILES} ملف`);
      break;
    }
    if (file.size > MAX_FILE_SIZE) {
      errors.push(`${file.name} أكبر من 200MB`);
      continue;
    }
    if (!VALID_TYPES.includes(file.type) && !file.type.startsWith("image/")) {
      errors.push(`${file.name} نوع غير مدعوم`);
      continue;
    }
    valid.push(file);
  }

  return { valid, errors };
}
